import { FC, PointerEvent as ReactPointerEvent, useCallback, useEffect, useRef, useState } from 'react';
import { Box, IconButton, Typography } from '@mui/material';
import { observer } from 'mobx-react-lite';

import { useTrans } from '@libs/i18n';
import { CaretLeftIcon, CaretRightIcon } from '@phosphor-icons/react';

import type { ActiveTemplateCategoryMeta } from '../../api/types';
import { categoryIcon } from '../utils/categoryIcon';
import type { DerivedCategory } from '../utils/deriveCategoryStatus';
import { ORG_TOKENS } from '../utils/tokens';

interface Props {
  categories: ActiveTemplateCategoryMeta[];
  derivedById: Map<string, DerivedCategory>;
  activeId: string | null;
  onSelect: (categoryId: string) => void;
}

/** Horizontal category tabs in a white card, paged with arrow buttons and
 *  click-drag scrolling. Each tab shows a "filled / total" field counter. */
export const CategoryTabs: FC<Props> = observer(
  ({ categories, derivedById, activeId, onSelect }) => {
    const { t } = useTrans();

    const scrollRef = useRef<HTMLDivElement>(null);
    const [canLeft, setCanLeft] = useState(false);
    const [canRight, setCanRight] = useState(false);

    const updateArrows = useCallback(() => {
      const el = scrollRef.current;
      if (!el) return;
      setCanLeft(el.scrollLeft > 1);
      setCanRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 1);
    }, []);

    useEffect(() => {
      updateArrows();
      const el = scrollRef.current;
      if (!el) return;
      el.addEventListener('scroll', updateArrows, { passive: true });
      window.addEventListener('resize', updateArrows);
      return () => {
        el.removeEventListener('scroll', updateArrows);
        window.removeEventListener('resize', updateArrows);
      };
    }, [updateArrows, categories.length]);

    // Gentle, fixed step (~one tab) per arrow click rather than a full page.
    const SCROLL_STEP = 200;
    const page = (direction: 1 | -1) => {
      const el = scrollRef.current;
      if (!el) return;
      el.scrollBy({ left: direction * SCROLL_STEP, behavior: 'smooth' });
    };

    // ── Click-drag horizontal scrolling ───────────────────────────────────
    // `moved` distinguishes a drag from a click so a drag doesn't also select
    // the tab the pointer happened to start on.
    const drag = useRef({ active: false, startX: 0, startScroll: 0, moved: false });

    const onPointerDown = (e: ReactPointerEvent<HTMLDivElement>) => {
      // Touch/pen: let the browser handle native horizontal scroll.
      if (e.pointerType !== 'mouse') return;
      const el = scrollRef.current;
      if (!el) return;
      // Don't capture yet — capturing immediately breaks click events on child
      // buttons. Capture is deferred until the drag threshold is exceeded.
      drag.current = {
        active: true,
        startX: e.clientX,
        startScroll: el.scrollLeft,
        moved: false,
      };
    };

    const onPointerMove = (e: ReactPointerEvent<HTMLDivElement>) => {
      const el = scrollRef.current;
      const d = drag.current;
      if (!el || !d.active) return;
      const dx = e.clientX - d.startX;
      if (Math.abs(dx) > 5) {
        d.moved = true;
        // Capture only once the drag is confirmed — keeps click events intact.
        if (!el.hasPointerCapture(e.pointerId)) el.setPointerCapture(e.pointerId);
      }
      el.scrollLeft = d.startScroll - dx;
    };

    const endDrag = (e: ReactPointerEvent<HTMLDivElement>) => {
      const el = scrollRef.current;
      if (el?.hasPointerCapture(e.pointerId)) el.releasePointerCapture(e.pointerId);
      drag.current.active = false;
    };

    const arrowSx = {
      flex: 'none',
      alignSelf: 'center',
      width: 40,
      height: 40,
      color: 'text.secondary',
      '&:hover': { backgroundColor: 'grey.200' },
    } as const;

    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 1,
          p: 1,
          backgroundColor: 'background.paper',
          border: 1,
          borderColor: 'grey.300',
          borderRadius: `${ORG_TOKENS.radiusMd}px`,
        }}
      >
        <Typography
          variant="textXS_bold"
          color="text.tertiary"
          sx={{ textTransform: 'uppercase', px: 0.5 }}
        >
          {t('app.orgProfile.tabs.title')}
        </Typography>

        <Box sx={{ display: 'flex', alignItems: 'stretch', gap: 0.5 }}>
        <IconButton
          onClick={() => page(-1)}
          disabled={!canLeft}
          aria-label={t('app.orgProfile.tabs.scrollPrev')}
          sx={arrowSx}
        >
          <CaretLeftIcon size={20} />
        </IconButton>

        <Box
          ref={scrollRef}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={endDrag}
          onPointerCancel={endDrag}
          sx={{
            display: 'flex',
            gap: 1,
            flex: 1,
            minWidth: 0,
            overflowX: 'auto',
            scrollBehavior: 'smooth',
            cursor: 'grab',
            userSelect: 'none',
            touchAction: 'pan-x',
            scrollbarWidth: 'none',
            '&::-webkit-scrollbar': { display: 'none' },
            '&:active': { cursor: 'grabbing' },
          }}
        >
          {categories.map((category) => {
            const active = category.id === activeId;
            const derived = derivedById.get(category.id);
            const name = category.metadata.name || category.key;
            const IconCmp = categoryIcon(category.key, name);
            const filled = derived?.filledCount ?? category.fieldsFilled;
            const total = derived?.totalFields ?? category.totalFields;

            return (
              <Box
                key={category.id}
                component="button"
                type="button"
                onClick={() => {
                  // Suppress the click that ends a drag gesture.
                  if (drag.current.moved) return;
                  onSelect(category.id);
                }}
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 0.75,
                  flex: 'none',
                  px: 1,
                  py: 0.625,
                  borderRadius: `${ORG_TOKENS.radiusSm}px`,
                  cursor: 'pointer',
                  fontFamily: 'inherit',
                  textAlign: 'start',
                  backgroundColor: active ? 'text.primary' : 'background.paper',
                  border: 1,
                  borderColor: active ? 'text.primary' : 'grey.300',
                  transition: 'background-color 0.15s ease',
                  '&:hover': {
                    backgroundColor: active ? 'text.primary' : 'grey.200',
                  },
                }}
              >
                <Box
                  sx={{
                    width: 24,
                    height: 24,
                    borderRadius: 1,
                    flex: 'none',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: active ? 'background.paper' : 'text.secondary',
                    backgroundColor: active ? 'rgba(255,255,255,0.14)' : 'grey.200',
                  }}
                >
                  <IconCmp size={14} weight="regular" />
                </Box>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.2, minWidth: 0 }}>
                  <Typography
                    variant="textXS_bold"
                    color={active ? 'background.paper' : 'text.primary'}
                    sx={{ whiteSpace: 'nowrap' }}
                  >
                    {name}
                  </Typography>
                  <Typography
                    variant="textXS_semiBold"
                    sx={{
                      whiteSpace: 'nowrap',
                      color: active ? 'rgba(255,255,255,0.85)' : 'text.tertiary',
                    }}
                  >
                    {t('app.orgProfile.status.fieldCount', { filled, total })}
                  </Typography>
                </Box>
              </Box>
            );
          })}
        </Box>

        <IconButton
          onClick={() => page(1)}
          disabled={!canRight}
          aria-label={t('app.orgProfile.tabs.scrollNext')}
          sx={arrowSx}
        >
          <CaretRightIcon size={20} />
        </IconButton>
        </Box>
      </Box>
    );
  },
);
